import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sparkles, Package, Tag, Lightbulb, FolderTree, Plus, X, Search } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';
import { toast } from 'react-hot-toast';

interface SuggestedProduct {
  id: string;
  name: string;
  category: string;
  relevance: number;
  matchReason?: string;
}

interface SuggestedCategory {
  name: string;
  relevance: number;
  matchReason?: string;
}

interface AIWriterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  topic: {
    id: number;
    topic_name: string;
    topic_description?: string;
    category?: string; // Topic type classification
    topic_type?: string;
    top_categories?: Array<{ count: number; category: string }>; // Product categories this topic relates to
  };
  onArticleGenerated?: (article: any) => void;
}

export function AIWriterModal({
  open,
  onOpenChange,
  topic,
  onArticleGenerated,
}: AIWriterModalProps) {
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [targetKeywords, setTargetKeywords] = useState('');
  const [keywordDensity, setKeywordDensity] = useState([2]);
  const [articleLength, setArticleLength] = useState('1000');
  const [tone, setTone] = useState('professional');
  const [perspective, setPerspective] = useState('first-person-plural');
  const [ctaStyle, setCtaStyle] = useState('soft-sell');

  // Polling cleanup ref
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!open && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [open]);

  // Semantic suggestions state
  const [suggestedProducts, setSuggestedProducts] = useState<SuggestedProduct[]>([]);
  const [suggestedCategories, setSuggestedCategories] = useState<SuggestedCategory[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ products: any[]; categories: string[] }>({ products: [], categories: [] });
  const [loadingSearch, setLoadingSearch] = useState(false);

  // Fetch products, categories, and semantic suggestions when modal opens
  useEffect(() => {
    if (open && selectedSite) {
      fetchProducts();
      fetchSemanticSuggestions();
    }
  }, [open, selectedSite, topic]);

  const fetchProducts = async () => {
    try {
      const response = await authFetch(`/api/ai-writer/products?siteId=${selectedSite?.id || selectedSite?._id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      const data = await response.json();

      // Handle new response format with products and categories
      if (data.products && data.categories) {
        setProducts(data.products);
        setCategories(data.categories);
      } else {
        // Fallback for old format (just array of products)
        const productsArray = Array.isArray(data) ? data : [];
        setProducts(productsArray);
        // Extract unique categories
        const uniqueCategories = [...new Set(productsArray.map((p: any) => p.category).filter(Boolean))];
        setCategories(uniqueCategories as string[]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      setCategories([]);
    }
  };

  // Fetch semantically similar products/categories for the topic
  const fetchSemanticSuggestions = async () => {
    if (!selectedSite || !topic) return;

    setLoadingSuggestions(true);
    try {
      // Extract product categories from top_categories (sorted by count)
      const productCategories = (topic.top_categories || [])
        .sort((a, b) => b.count - a.count)
        .map(c => c.category)
        .slice(0, 3); // Top 3 most relevant categories

      // Build a richer query combining topic name, description, and product categories
      const topicContext = [
        topic.topic_name,
        topic.topic_description,
        productCategories.length > 0 ? `Related product categories: ${productCategories.join(', ')}` : '',
      ].filter(Boolean).join('. ');

      const response = await authFetch('/api/ai-writer/semantic-match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id || selectedSite._id,
          topicText: topicContext, // Use enriched context
          topicDescription: topic.topic_description || '',
          keywords: [], // Research topics may not have keywords
          relatedCategories: productCategories, // Use actual product categories for filtering
          limit: 10,
        }),
      });

      if (response.ok) {
        const { products: matchedProducts, categories: matchedCategories } = await response.json();
        setSuggestedProducts(matchedProducts || []);
        setSuggestedCategories(matchedCategories || []);

        // Auto-select top suggested products and categories
        if (matchedProducts?.length > 0) {
          const topProducts = matchedProducts
            .filter((p: SuggestedProduct) => p.relevance >= 0.7)
            .slice(0, 3)
            .map((p: SuggestedProduct) => p.id);
          setSelectedProducts(topProducts);
        }
        if (matchedCategories?.length > 0) {
          const topCategories = matchedCategories
            .filter((c: SuggestedCategory) => c.relevance >= 0.7)
            .slice(0, 2)
            .map((c: SuggestedCategory) => c.name);
          setSelectedCategories(topCategories);
        }
      }
    } catch (error) {
      console.error('Error fetching semantic suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Search products and categories by text
  const handleSearch = async () => {
    if (!searchQuery.trim() || !selectedSite) return;

    setLoadingSearch(true);
    try {
      const response = await authFetch(`/api/ai-writer/search?siteId=${selectedSite.id || selectedSite._id}&query=${encodeURIComponent(searchQuery)}&limit=20`);

      if (response.ok) {
        const results = await response.json();
        setSearchResults({
          products: results.products || [],
          categories: results.categories || [],
        });
      }
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleProductToggle = (productId: string) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const selectAllInCategory = (category: string) => {
    const categoryProducts = products
      .filter((p) => p.category === category)
      .map((p) => p.id);
    
    setSelectedProducts((prev) => {
      const newSelection = new Set(prev);
      categoryProducts.forEach((id) => newSelection.add(id));
      return Array.from(newSelection);
    });
  };

  const handleGenerate = async () => {
    if (!selectedSite) {
      toast.error('Please select a site first');
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch('/api/ai-writer/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topicId: topic.id,
          siteId: selectedSite.id || selectedSite._id,
          selectedProducts,
          selectedCategories,
          targetKeywords: targetKeywords.split(',').map((k) => k.trim()),
          articleLength: parseInt(articleLength),
          tone,
          perspective,
          ctaStyle,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start article generation');
      }

      const { jobId } = await response.json();

      // Poll for completion
      pollIntervalRef.current = setInterval(async () => {
        try {
          const pollResponse = await authFetch(`/api/ai-writer/jobs/${jobId}`);
          if (!pollResponse.ok) throw new Error('Failed to check job status');

          const jobData = await pollResponse.json();

          if (jobData.status === 'completed' && jobData.article) {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setLoading(false);
            toast.success('Article generated successfully!');
            if (onArticleGenerated) {
              onArticleGenerated(jobData.article);
            }
            onOpenChange(false);
          } else if (jobData.status === 'failed') {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setLoading(false);
            toast.error(jobData.error_message || 'Article generation failed.');
          }
        } catch (pollError) {
          console.error('Polling error:', pollError);
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setLoading(false);
          toast.error('Lost connection while generating. Check the articles list.');
        }
      }, 3000);

    } catch (error) {
      console.error('Error generating article:', error);
      toast.error('Failed to generate article. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Writer - Generate Article
          </DialogTitle>
          <DialogDescription>
            Generate an SEO-optimized blog article about "{topic.topic_name}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Product Selection */}
          <div>
            <Label className="text-base font-semibold mb-3 block">
              Select Products to Feature
            </Label>

            <ScrollArea className="h-[350px]">
              <div className="space-y-4 pr-4">
                {/* AI Suggested Products Section */}
                {loadingSuggestions ? (
                  <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/30">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                        <span className="text-sm text-blue-700 dark:text-blue-300">
                          Finding best products for "{topic.topic_name}"...
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ) : suggestedProducts.length > 0 && (
                  <Card className="border-green-200 bg-green-50 dark:bg-green-950/30">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Lightbulb className="h-4 w-4 text-green-600" />
                        Suggested Products for This Topic
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-2">
                      {suggestedProducts.slice(0, 5).map((product) => (
                        <div key={product.id} className="flex items-center justify-between p-2 rounded-md bg-white dark:bg-gray-800 border">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`suggested-${product.id}`}
                              checked={selectedProducts.includes(product.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedProducts([...selectedProducts, product.id]);
                                } else {
                                  setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                                }
                              }}
                            />
                            <div>
                              <label
                                htmlFor={`suggested-${product.id}`}
                                className="text-sm font-medium cursor-pointer"
                              >
                                {product.name}
                              </label>
                              <p className="text-xs text-muted-foreground">{product.category}</p>
                            </div>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {Math.round(product.relevance * 100)}% match
                          </Badge>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* AI Suggested Categories Section */}
                {suggestedCategories.length > 0 && !loadingSuggestions && (
                  <Card className="border-purple-200 bg-purple-50 dark:bg-purple-950/30">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FolderTree className="h-4 w-4 text-purple-600" />
                        Suggested Categories for This Topic
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 flex flex-wrap gap-2">
                      {suggestedCategories.slice(0, 5).map((category) => (
                        <Badge
                          key={category.name}
                          variant={selectedCategories.includes(category.name) ? 'default' : 'outline'}
                          className="cursor-pointer py-1.5 px-3"
                          onClick={() => {
                            if (selectedCategories.includes(category.name)) {
                              setSelectedCategories(selectedCategories.filter(c => c !== category.name));
                            } else {
                              setSelectedCategories([...selectedCategories, category.name]);
                            }
                          }}
                        >
                          {selectedCategories.includes(category.name) ? (
                            <X className="h-3 w-3 mr-1" />
                          ) : (
                            <Plus className="h-3 w-3 mr-1" />
                          )}
                          {category.name}
                          <span className="ml-1.5 text-xs opacity-70">
                            {Math.round(category.relevance * 100)}%
                          </span>
                        </Badge>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Search Section */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Search className="h-4 w-4" />
                      Search Products & Categories
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search by name, brand, or category..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      />
                      <Button onClick={handleSearch} size="sm" disabled={loadingSearch}>
                        {loadingSearch ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Search className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    {/* Search Results */}
                    {(searchResults.products.length > 0 || searchResults.categories.length > 0) && (
                      <div className="space-y-2 border rounded-lg p-3 bg-muted/50">
                        {searchResults.products.length > 0 && (
                          <div>
                            <Label className="text-xs text-muted-foreground mb-2 block">Products</Label>
                            <div className="space-y-1.5 max-h-32 overflow-y-auto">
                              {searchResults.products.slice(0, 10).map((product) => (
                                <div key={product.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`search-${product.id}`}
                                    checked={selectedProducts.includes(product.id)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setSelectedProducts([...selectedProducts, product.id]);
                                      } else {
                                        setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                                      }
                                    }}
                                  />
                                  <label
                                    htmlFor={`search-${product.id}`}
                                    className="text-sm leading-none cursor-pointer"
                                  >
                                    {product.name}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {searchResults.categories.length > 0 && (
                          <div>
                            <Label className="text-xs text-muted-foreground mb-2 block">Categories</Label>
                            <div className="flex flex-wrap gap-1.5">
                              {searchResults.categories.slice(0, 10).map((category) => (
                                <Badge
                                  key={category}
                                  variant={selectedCategories.includes(category) ? 'default' : 'outline'}
                                  className="cursor-pointer text-xs"
                                  onClick={() => {
                                    if (selectedCategories.includes(category)) {
                                      setSelectedCategories(selectedCategories.filter(c => c !== category));
                                    } else {
                                      setSelectedCategories([...selectedCategories, category]);
                                    }
                                  }}
                                >
                                  {category}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Selected Items Summary */}
                {(selectedProducts.length > 0 || selectedCategories.length > 0) && (
                  <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/30">
                    <CardContent className="py-3">
                      <div className="flex flex-wrap gap-2">
                        {selectedProducts.length > 0 && (
                          <Badge variant="outline" className="text-amber-700">
                            <Package className="h-3 w-3 mr-1" />
                            {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
                          </Badge>
                        )}
                        {selectedCategories.length > 0 && (
                          <Badge variant="outline" className="text-amber-700">
                            <FolderTree className="h-3 w-3 mr-1" />
                            {selectedCategories.length} categor{selectedCategories.length > 1 ? 'ies' : 'y'} selected
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* All Products (collapsible list) */}
                <Tabs defaultValue="products" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="products">
                      <Package className="h-4 w-4 mr-2" />
                      All Products
                    </TabsTrigger>
                    <TabsTrigger value="categories">
                      <Tag className="h-4 w-4 mr-2" />
                      All Categories
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="products" className="max-h-48 overflow-y-auto border rounded-lg p-3">
                    <div className="space-y-2">
                      {products.map((product) => (
                        <div key={product.id} className="flex items-start space-x-2">
                          <Checkbox
                            id={`product-${product.id}`}
                            checked={selectedProducts.includes(product.id)}
                            onCheckedChange={() => handleProductToggle(product.id)}
                          />
                          <label
                            htmlFor={`product-${product.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            <div>{product.name}</div>
                            {product.price && (
                              <div className="text-xs text-muted-foreground">
                                ${product.price}
                              </div>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="categories" className="max-h-48 overflow-y-auto border rounded-lg p-3">
                    <div className="space-y-3">
                      {categories.map((category) => (
                        <div key={category} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-start space-x-2">
                              <Checkbox
                                id={`category-${category}`}
                                checked={selectedCategories.includes(category)}
                                onCheckedChange={() => handleCategoryToggle(category)}
                              />
                              <label
                                htmlFor={`category-${category}`}
                                className="text-sm font-medium leading-none cursor-pointer"
                              >
                                {category}
                              </label>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => selectAllInCategory(category)}
                            >
                              Select all
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </ScrollArea>
          </div>

          {/* SEO Configuration */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">SEO Configuration</Label>
            
            <div>
              <Label htmlFor="keywords">Target Keywords (comma-separated)</Label>
              <Input
                id="keywords"
                placeholder="educational toys, STEM learning, kids development"
                value={targetKeywords}
                onChange={(e) => setTargetKeywords(e.target.value)}
              />
            </div>

            <div>
              <Label>Keyword Density: {keywordDensity[0]}%</Label>
              <Slider
                value={keywordDensity}
                onValueChange={setKeywordDensity}
                min={1}
                max={3}
                step={0.5}
                className="mt-2"
              />
              <div className="text-xs text-muted-foreground mt-1">
                Recommended: 1-2% for natural reading
              </div>
            </div>

            <div>
              <Label htmlFor="length">Article Length</Label>
              <Select value={articleLength} onValueChange={setArticleLength}>
                <SelectTrigger id="length">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="500">500 words</SelectItem>
                  <SelectItem value="750">750 words</SelectItem>
                  <SelectItem value="1000">1000 words</SelectItem>
                  <SelectItem value="1500">1500 words</SelectItem>
                  <SelectItem value="2000">2000 words</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tone & Style */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Tone & Style</Label>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="tone">Writing Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger id="tone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="enthusiastic">Enthusiastic</SelectItem>
                    <SelectItem value="educational">Educational</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="perspective">Perspective</Label>
                <Select value={perspective} onValueChange={setPerspective}>
                  <SelectTrigger id="perspective">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first-person-plural">We/Our</SelectItem>
                    <SelectItem value="third-person">Third Person</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="cta">CTA Style</Label>
                <Select value={ctaStyle} onValueChange={setCtaStyle}>
                  <SelectTrigger id="cta">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="soft-sell">Soft Sell</SelectItem>
                    <SelectItem value="direct">Direct</SelectItem>
                    <SelectItem value="informational">Informational</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-muted p-4 rounded-lg">
            <div className="text-sm space-y-1">
              <div>
                <strong>Topic:</strong> {topic.topic_name}
              </div>
              <div>
                <strong>Products:</strong> {selectedProducts.length} selected
              </div>
              <div>
                <strong>Categories:</strong> {selectedCategories.length} selected
              </div>
              <div>
                <strong>Keywords:</strong>{' '}
                {targetKeywords
                  ? targetKeywords.split(',').map((k) => k.trim()).join(', ')
                  : 'None'}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerate} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Article
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}